/**
 * Subscription Service
 * Handles subscription status checking, promotions, and checkout flow
 */

import { telegram } from './telegram';

const API_BASE = '/api/subscription';

export interface SubscriptionStatus {
  isSubscribed: boolean;
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired' | 'error';
  type?: 'monthly' | 'yearly' | 'business_plus';
  expiresAt?: string;
  isTrialing: boolean;
  trialDaysRemaining: number;
  canTrial: boolean;
  // Business Plus specific
  isBusinessPlus?: boolean;
  businessPlusFeatures?: string[];
  companyName?: string;
  companyRole?: string;
}

export interface PromoValidation {
  valid: boolean;
  code?: string;
  description?: string;
  discountType?: 'percent' | 'fixed';
  discountValue?: number;
  discountDisplay?: string;
  appliesTo?: 'all' | 'monthly' | 'yearly';
  expiresAt?: string;
  error?: string;
}

// Plan ID types - includes both new and legacy IDs for backward compatibility
export type NewPlanId = 'individual_monthly' | 'individual_yearly' | 'business_starter' | 'business_team' | 'business_fleet' | 'business_enterprise';
export type LegacyPlanId = 'monthly' | 'yearly' | 'business_plus';
export type PlanId = NewPlanId | LegacyPlanId;

// Map legacy IDs to new IDs
export const LEGACY_PLAN_MAP: Record<LegacyPlanId, NewPlanId> = {
  'monthly': 'individual_monthly',
  'yearly': 'individual_yearly', 
  'business_plus': 'business_starter'
};

export interface PricingPlan {
  id: PlanId;
  name: string;
  price: number;
  originalPrice?: number;
  interval: 'month' | 'year';
  trialDays: number;
  features: string[];
  popular?: boolean;
  savings?: string;
  tier: 'individual' | 'business';
  badge?: string;
  minUsers?: number;
  maxUsers?: number;
  charityPercent?: number; // % going to Packs for Hunger
}

export interface BusinessTier {
  id: 'starter' | 'team' | 'fleet' | 'enterprise';
  name: string;
  pricePerUser: number;
  minUsers: number;
  maxUsers: number | null; // null = unlimited
  charityPercent: number;
}

export const BUSINESS_TIERS: BusinessTier[] = [
  { id: 'starter', name: 'Starter', pricePerUser: 7, minUsers: 5, maxUsers: 25, charityPercent: 20 },
  { id: 'team', name: 'Team', pricePerUser: 6, minUsers: 26, maxUsers: 100, charityPercent: 20 },
  { id: 'fleet', name: 'Fleet', pricePerUser: 5, minUsers: 101, maxUsers: 250, charityPercent: 20 },
  { id: 'enterprise', name: 'Enterprise', pricePerUser: 4, minUsers: 251, maxUsers: null, charityPercent: 20 },
];

export function getBusinessTierForUsers(userCount: number): BusinessTier | null {
  if (userCount < 5) return null;
  return BUSINESS_TIERS.find(tier => 
    userCount >= tier.minUsers && (tier.maxUsers === null || userCount <= tier.maxUsers)
  ) || BUSINESS_TIERS[BUSINESS_TIERS.length - 1]; // Default to enterprise for 251+
}

export function calculateBusinessPrice(userCount: number): { tier: BusinessTier; totalMonthly: number; perUser: number } | null {
  const tier = getBusinessTierForUsers(userCount);
  if (!tier) return null;
  return {
    tier,
    totalMonthly: userCount * tier.pricePerUser,
    perUser: tier.pricePerUser
  };
}

class SubscriptionService {
  private cachedStatus: SubscriptionStatus | null = null;
  private cacheTimestamp: number = 0;
  private cacheDuration = 60000; // 1 minute cache

  private getUserHeaders() {
    const user = telegram.getUser();
    return {
      'X-Telegram-User-Id': telegram.getUserId() || '',
      'X-Telegram-Username': user?.username || user?.first_name || '',
      'X-Telegram-Init-Data': telegram.getInitData()
    };
  }

  /**
   * Get current subscription status
   */
  async getStatus(forceRefresh = false): Promise<SubscriptionStatus> {
    // Return cached status if valid
    if (!forceRefresh && this.cachedStatus && Date.now() - this.cacheTimestamp < this.cacheDuration) {
      return this.cachedStatus;
    }

    try {
      const response = await fetch(API_BASE, {
        headers: this.getUserHeaders()
      });

      const status = await response.json();
      
      this.cachedStatus = status;
      this.cacheTimestamp = Date.now();
      
      return status;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      return {
        isSubscribed: false,
        status: 'error',
        isTrialing: false,
        trialDaysRemaining: 0,
        canTrial: true
      };
    }
  }

  /**
   * Check if user is subscribed (active or trialing)
   */
  async isSubscribed(): Promise<boolean> {
    const status = await this.getStatus();
    return status.isSubscribed;
  }

  /**
   * Validate a promo code
   */
  async validatePromo(code: string): Promise<PromoValidation> {
    try {
      const response = await fetch(`${API_BASE}/promo?code=${encodeURIComponent(code)}`, {
        headers: this.getUserHeaders()
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to validate promo:', error);
      return { valid: false, error: 'Failed to validate promo code' };
    }
  }

  /**
   * Apply a promo code to user's account
   */
  async applyPromo(code: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await fetch(`${API_BASE}/promo`, {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Failed to apply promo:', error);
      return { success: false, error: 'Failed to apply promo code' };
    }
  }

  /**
   * Initiate checkout - Opens Telegram bot for secure payment
   */
  async initiateCheckout(plan: PlanId): Promise<void> {
    // Open the bot with /subscribe command
    // This is the secure way to handle payments in Telegram Mini Apps
    const botUsername = 'ZiaWellNavBot';
    const botUrl = `https://t.me/${botUsername}?start=subscribe_${plan}`;
    
    // Try multiple methods to open the bot
    const tg = (window as any).Telegram?.WebApp;
    
    try {
      // Method 1: Use Telegram's openTelegramLink (most reliable in Telegram)
      if (tg?.openTelegramLink) {
        console.log('[Subscription] Opening via openTelegramLink:', botUrl);
        tg.openTelegramLink(botUrl);
        return;
      }
      
      // Method 2: Use openLink for external URLs
      if (tg?.openLink) {
        console.log('[Subscription] Opening via openLink:', botUrl);
        tg.openLink(botUrl, { try_instant_view: false });
        return;
      }
      
      // Method 3: Direct window.open fallback
      console.log('[Subscription] Opening via window.open:', botUrl);
      window.open(botUrl, '_blank');
    } catch (error) {
      console.error('[Subscription] Failed to open bot:', error);
      // Ultimate fallback - try window.location
      window.location.href = botUrl;
    }
  }

  /**
   * Get pricing plans
   */
  getPricingPlans(): PricingPlan[] {
    return [
      {
        id: 'individual_monthly',
        name: 'Individual Monthly',
        price: 10.00,
        interval: 'month',
        trialDays: 7,
        tier: 'individual',
        features: [
          '🗺️ Unlimited facility navigation',
          '📍 Full offline cache access',
          '🔊 Voice navigation & search',
          '📢 Basin channel access',
          '💬 Community support',
          '🏥 424,000+ wells (NM & TX)'
        ]
      },
      {
        id: 'individual_yearly',
        name: 'Individual Annual',
        price: 100.00,
        originalPrice: 120.00,
        interval: 'year',
        trialDays: 7,
        popular: true,
        savings: '2 months FREE!',
        tier: 'individual',
        features: [
          '🗺️ Unlimited facility navigation',
          '📍 Full offline cache access',
          '🔊 Voice navigation & search',
          '📢 Basin channel access',
          '💬 Priority support',
          '🏥 424,000+ wells (NM & TX)',
          '🎁 2 months FREE ($20 savings)'
        ]
      }
    ];
  }

  /**
   * Get Business pricing display info
   */
  getBusinessPricingInfo() {
    return {
      tiers: BUSINESS_TIERS,
      charityPercent: 20,
      charityName: 'Packs for Hunger',
      features: [
        '🏢 All Individual features',
        '👥 Team management dashboard',
        '📊 Usage analytics & reporting',
        '🔐 Admin controls & permissions',
        '📱 Bulk license management',
        '💼 Company verification badge',
        '🎯 Priority enterprise support',
        '💚 20% goes to Packs for Hunger'
      ]
    };
  }

  /**
   * Get Business Plus pricing plans (legacy - use getBusinessPricingInfo instead)
   */
  getBusinessPlusPlan(): PricingPlan {
    return {
      id: 'business_starter',
      name: 'Business Starter',
      price: 7.00,
      interval: 'month',
      trialDays: 7,
      tier: 'business',
      badge: 'TEAM',
      minUsers: 5,
      maxUsers: 25,
      charityPercent: 20,
      features: [
        '🏢 Everything in Individual',
        '👥 5-25 user licenses',
        '📊 Team analytics dashboard',
        '🔐 Admin controls',
        '💼 Company verification badge',
        '💚 20% to Packs for Hunger'
      ]
    };
  }

  /**
   * Initiate Stripe checkout for individual plans
   */
  async initiateStripeCheckout(planId: 'individual_monthly' | 'individual_yearly', successUrl?: string, cancelUrl?: string): Promise<{ checkoutUrl?: string; error?: string }> {
    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          planId,
          successUrl: successUrl || `${window.location.origin}/app?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`
        })
      });
      
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Stripe checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Initiate Stripe checkout for business plans
   */
  async initiateBusinessCheckout(userCount: number, successUrl?: string, cancelUrl?: string): Promise<{ checkoutUrl?: string; error?: string }> {
    const pricing = calculateBusinessPrice(userCount);
    if (!pricing) {
      return { error: 'Minimum 5 users required for business plans' };
    }

    try {
      const response = await fetch('/api/subscription/stripe-checkout', {
        method: 'POST',
        headers: {
          ...this.getUserHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          planId: `business_${pricing.tier.id}`,
          userCount,
          successUrl: successUrl || `${window.location.origin}/app?checkout=success`,
          cancelUrl: cancelUrl || `${window.location.origin}/?checkout=canceled`
        })
      });
      
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
      return data;
    } catch (error) {
      console.error('Business checkout error:', error);
      return { error: 'Failed to create checkout session' };
    }
  }

  /**
   * Check if user has Business Plus subscription
   */
  async isBusinessPlus(): Promise<boolean> {
    const status = await this.getStatus();
    return status.isBusinessPlus === true || status.type === 'business_plus';
  }

  /**
   * Get subscription benefits for display
   */
  getSubscriptionBenefits(): { icon: string; title: string; description: string }[] {
    return [
      {
        icon: '🗺️',
        title: 'Full Navigation',
        description: 'Navigate to any well, SWD, rig, or facility in New Mexico'
      },
      {
        icon: '📱',
        title: 'Offline Access',
        description: 'Download entire basins for offline use in remote areas'
      },
      {
        icon: '🔊',
        title: 'Voice Commands',
        description: 'Hands-free voice search and turn-by-turn directions'
      },
      {
        icon: '📢',
        title: 'Basin Channels',
        description: 'Access private channels for road conditions & safety alerts'
      },
      {
        icon: '🛤️',
        title: 'Caliche Routes',
        description: 'Community-shared routes to avoid bad roads'
      },
      {
        icon: '📸',
        title: 'Site Photos',
        description: 'View and share photos to identify locations visually'
      }
    ];
  }

  /**
   * Clear cached status (call after subscription changes)
   */
  clearCache(): void {
    this.cachedStatus = null;
    this.cacheTimestamp = 0;
  }
}

export const subscriptionService = new SubscriptionService();
